<template>
    <DrawerRoot
        v-model:open="isOpen"
        v-model:snap-point="activeSnapPoint"
        :snap-points="snapPoints"
        :modal="false"
        swipe-direction="down"
        @update:open="onOpenChange"
    >
        <DrawerPortal>
            <!-- interact-outside.prevent: the sheet is a permanent part of the page, so a press elsewhere must not collapse it. -->
            <DrawerContent
                ref="contentRef"
                class="bottom-sheet-content fixed right-0 left-0 z-30 flex flex-col rounded-t-3xl bg-white/99 shadow-[0_-4px_16px_rgba(0,0,0,0.10)] outline-none md:hidden"
                :class="{ hidden: isHidden }"
                :style="{ bottom: `${footerHeight}px` }"
                @interact-outside.prevent
            >
                <DrawerTitle class="sr-only">
                    <slot name="title">Panel</slot>
                </DrawerTitle>

                <Transition
                    enter-active-class="transition-opacity duration-300"
                    enter-from-class="opacity-0"
                    leave-active-class="transition-opacity duration-150"
                    leave-to-class="opacity-0"
                >
                    <div
                        v-if="isCollapsed && !hasExpandedOnce"
                        class="pointer-events-none absolute inset-x-0 -top-8 flex justify-center"
                        aria-hidden="true"
                    >
                        <ChevronsUpIcon class="bottom-sheet-chevron size-5 text-gray-400" />
                    </div>
                </Transition>

                <DrawerHandle class="mx-auto mt-2 mb-1 h-1 w-10 shrink-0 rounded-full bg-gray-300" />

                <!-- No native drag while collapsed: a downward swipe on the panel text must not dismiss the sheet. -->
                <div ref="contentTopRef" :data-vaul-no-drag="isCollapsed ? '' : undefined">
                    <slot />
                </div>
            </DrawerContent>
        </DrawerPortal>
    </DrawerRoot>

    <!-- iOS Safari 26.0 bug: a fixed element with a solid background clips around the floating toolbar, so the background lives on a near-opaque (99%) absolutely positioned child instead. -->
    <div ref="footerRef" class="fixed right-0 bottom-0 left-0 z-40 bg-transparent md:hidden" :class="{ hidden: isHidden }">
        <div class="pointer-events-none absolute inset-x-0 top-0 -bottom-[50vh] border-t border-border bg-white/99" aria-hidden="true" />

        <!-- Positioned sibling, or the background child covers the footer content. -->
        <div class="relative">
            <slot name="footer" />
        </div>
    </div>
</template>

<!-- Plain script block: this state must outlive component instances, and <script setup> rebuilds it per instance. -->
<script lang="ts">
type PersistedSheetState = {
    isCollapsed: boolean;
    hasExpandedOnce: boolean;
};

// Module-level so the sheet survives a product switch (an Inertia visit); a fresh page load still starts expanded.
const persistedSheetStates = new Map<string, PersistedSheetState>();
</script>

<script setup lang="ts">
import { ChevronsUpIcon } from '@lucide/vue';
import { unrefElement } from '@vueuse/core';
import { DrawerContent, DrawerHandle, DrawerPortal, DrawerRoot, DrawerTitle } from 'reka-ui';
import { computed, onMounted, onUnmounted, ref, watch, type ComponentPublicInstance } from 'vue';

const props = withDefaults(
    defineProps<{
        expandedSnapPoint?: number;
        // display:none rather than a transform, so another full-height surface can own the bottom while the sheet stays mounted.
        isHidden?: boolean;
        expandRequests?: number;
        persistKey?: string;
    }>(),
    {
        expandedSnapPoint: undefined,
        isHidden: false,
        expandRequests: 0,
        persistKey: undefined,
    },
);

const footerRef = ref<HTMLElement | undefined>(undefined);

const contentTopRef = ref<HTMLElement | undefined>(undefined);
const contentRef = ref<ComponentPublicInstance | undefined>(undefined);
const isOpen = ref(true);
const footerHeight = ref(0);
// Collapsed point in pixels, not a fraction: a viewport fraction rounds to whole percents (~8px), leaving content peeking out.
const snapPoints = ref<Array<number | string>>(['28px', props.expandedSnapPoint ?? 0.45]);
const activeSnapPoint = ref<number | string>(props.expandedSnapPoint ?? 0.45);
const hasExpandedOnce = ref(false);
let footerResizeObserver: ResizeObserver | undefined = undefined;

const isCollapsed = computed<boolean>(() => activeSnapPoint.value === snapPoints.value[0]);

// Transition is off in CSS and only enabled during a snap movement: the drawer also rewrites its offset on mount/resize, which must not animate.
const animatedClass = 'bottom-sheet-content--animated';
const snapAnimationMs = 450;
let animationTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

// Set while mount restores the previous position — that move must not animate.
let isRestoring = false;

// flush: 'sync' — the drawer sets its offset in the same tick as the snap point, so waiting for a re-render is too late.
watch(
    activeSnapPoint,
    (point: number | string) => {
        if (point !== snapPoints.value[0]) {
            hasExpandedOnce.value = true;
        }

        rememberState(point);

        if (isRestoring) {
            return;
        }

        const element = unrefElement(contentRef);

        if (!(element instanceof HTMLElement)) {
            return;
        }

        element.classList.add(animatedClass);
        clearTimeout(animationTimeout);
        animationTimeout = setTimeout(() => element.classList.remove(animatedClass), snapAnimationMs);
    },
    { flush: 'sync' },
);

function rememberState(point: number | string): void {
    if (!props.persistKey) {
        return;
    }

    persistedSheetStates.set(props.persistKey, {
        isCollapsed: point === snapPoints.value[0],
        hasExpandedOnce: hasExpandedOnce.value,
    });
}

// Restores before first paint, so a sheet left collapsed doesn't slide shut in front of the customer.
function restoreState(collapsed: number | string, expanded: number): void {
    const persisted = props.persistKey ? persistedSheetStates.get(props.persistKey) : undefined;

    isRestoring = true;
    hasExpandedOnce.value = persisted?.hasExpandedOnce ?? false;
    activeSnapPoint.value = persisted?.isCollapsed ? collapsed : expanded;
    isRestoring = false;
}

watch(
    () => props.expandRequests,
    (requests: number, previousRequests: number | undefined) => {
        if (requests === 0 || requests === previousRequests) {
            return;
        }

        activeSnapPoint.value = snapPoints.value[1];
    },
);

function onOpenChange(open: boolean): void {
    if (!open) {
        isOpen.value = true;
        activeSnapPoint.value = snapPoints.value[0];
    }
}

// Observed rather than measured once: the footer's content changes height, and the sheet must stay seated on it.
function observeFooterHeight(footer: HTMLElement): void {
    footerHeight.value = footer.offsetHeight;

    footerResizeObserver = new ResizeObserver(() => {
        footerHeight.value = footer.offsetHeight;
    });

    footerResizeObserver.observe(footer);
}

// Handle strip height (handle + margins); falls back to the design height when there is no layout to measure (jsdom, pre-paint).
function resolveHandleStripHeight(): number {
    const measured = contentTopRef.value?.offsetTop ?? 0;

    return measured > 0 ? measured : 28;
}

onMounted(() => {
    if (!footerRef.value) {
        return;
    }

    observeFooterHeight(footerRef.value);

    const expanded = props.expandedSnapPoint ?? 0.45;
    const collapsed = `${resolveHandleStripHeight()}px`;
    snapPoints.value = [collapsed, expanded];

    restoreState(collapsed, expanded);
});

onUnmounted(() => {
    clearTimeout(animationTimeout);
    footerResizeObserver?.disconnect();
});
</script>

<style>
.bottom-sheet-content {
    transform: translateY(max(0px, calc(var(--drawer-snap-point-offset, 0px) + var(--drawer-swipe-movement-y, 0px))));
}

/* Added for the length of a snap movement only — see the watcher on activeSnapPoint. */
.bottom-sheet-content--animated {
    transition: transform 0.4s cubic-bezier(0.32, 0.72, 0, 1);
}

.bottom-sheet-content[data-swiping] {
    transition: none;
}

@keyframes bottom-sheet-wave {
    0%,
    100% {
        transform: translateY(0);
        opacity: 0.5;
    }
    50% {
        transform: translateY(-6px);
        opacity: 1;
    }
}

.bottom-sheet-chevron {
    animation: bottom-sheet-wave 1.6s ease-in-out infinite;
}
</style>
